import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar as CalendarIcon, Video, MonitorPlay } from 'lucide-react';
import { goPro } from '../../lib/checkout';
import { eventState, minutesLeft, LiveBadge } from '../Calendar';

// Friendly "in 2h" / "tomorrow at 5pm" label for the class card.
export function relativeLabel(iso, now = new Date()) {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMs = d - now;
  const diffMin = Math.round(diffMs / 60000);
  const diffDays = Math.floor((new Date(d.toDateString()) - new Date(now.toDateString())) / 86400000);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: d.getMinutes() ? '2-digit' : undefined, hour12: true }).toLowerCase().replace(' ', '');
  if (diffMs < 0) return `Started ${Math.abs(diffMin)} min ago`;
  if (diffMin < 60) return `In ${diffMin} min`;
  if (diffDays === 0) return `Today at ${time}`;
  if (diffDays === 1) return `Tomorrow at ${time}`;
  if (diffDays < 7) return `${d.toLocaleString([], { weekday: 'long' })} at ${time}`;
  return `${d.toLocaleString([], { month: 'short', day: 'numeric' })} at ${time}`;
}
const extUrl = (u) => { u = String(u || '').trim(); return !u ? '#' : /^[a-z][a-z0-9+.-]*:/i.test(u) ? u : u.startsWith('//') ? `https:${u}` : u.startsWith('/') ? u : `https://${u}`; };

/** Dashboard: the class running RIGHT NOW (highlighted, “Join now”) or the next one. */
export function NextClassCard({ ev }) {
  const navigate = useNavigate();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30_000); return () => clearInterval(t); }, []);
  const state = eventState(ev, now);
  const live = state === 'live';
  const join = () => (ev.in_app ? navigate(`/live/class/${ev.id}`) : window.open(extUrl(ev.zoom_link), '_blank', 'noopener,noreferrer'));
  const canJoin = !ev.is_locked && (ev.in_app || ev.zoom_link);
  return (
    <div className={`rounded-2xl border p-5 ${live ? 'bg-rose-50 border-rose-300 ring-2 ring-rose-200 shadow-[0_6px_24px_rgba(225,29,72,0.15)]' : 'bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]'}`} data-testid="upcoming-event-card" data-state={state}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className={`flex flex-col items-center justify-center rounded-xl px-3 py-2 min-w-[72px] border shrink-0 ${live ? 'bg-rose-600 text-white border-rose-600' : 'bg-blue-50 text-blue-700 border-blue-100'}`}>
          <span className="text-[10px] font-semibold uppercase">{new Date(ev.start_at).toLocaleString([], { month: 'short' })}</span>
          <span className="text-2xl font-bold tracking-tight leading-none">{new Date(ev.start_at).getDate()}</span>
        </div>
        <div className="flex-1 min-w-[180px]">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-[10px] font-semibold tracking-wider uppercase ${live ? 'text-rose-700' : 'text-blue-600'}`} data-testid="upcoming-event-kicker">{live ? 'Happening now' : state === 'soon' ? 'Starting soon' : 'Next Group Class'}</span>
            {live && <LiveBadge testId="upcoming-event-live-badge" />}
            {ev.access_tier === 'pro_only' && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-amber-100 text-amber-700">Pro</span>}
          </div>
          <h3 className="font-[Manrope] text-base font-bold text-slate-900 truncate" data-testid="upcoming-event-title">{ev.title}</h3>
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
            <CalendarIcon size={11} className="text-slate-400" />
            <span data-testid="upcoming-event-when">{live ? `Started ${Math.max(1, Math.round((now - new Date(ev.start_at)) / 60000))} min ago · ${minutesLeft(ev, now)} min left` : relativeLabel(ev.start_at, now)}</span>
            <span className="text-slate-300">·</span>
            <Video size={11} className={live ? 'text-rose-500' : 'text-blue-500'} />
            <span>{ev.location || 'Zoom'} · {ev.duration_minutes} min</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {ev.is_locked ? (
            <button onClick={() => goPro(navigate)} className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg px-4 py-2 transition" data-testid="upcoming-event-upgrade-btn"><Video size={13} /> Upgrade to join</button>
          ) : canJoin && (
            <button onClick={join} className={`inline-flex items-center gap-1.5 text-white text-sm font-semibold rounded-lg px-4 py-2 transition ${live ? 'bg-rose-600 hover:bg-rose-700 shadow-lg shadow-rose-600/25 animate-[pulse_2.5s_ease-in-out_infinite]' : 'bg-blue-600 hover:bg-blue-700'}`} data-testid="upcoming-event-join-btn" data-live={live ? 'true' : 'false'}>
              <MonitorPlay size={13} /> {live ? 'Join now' : ev.in_app ? 'Join class here' : 'Join class'}
            </button>
          )}
          <Link to="/group-classes" className="text-xs font-medium text-slate-500 hover:text-slate-700 hover:underline" data-testid="upcoming-event-all-classes">All classes →</Link>
        </div>
      </div>
    </div>
  );
}
